/* eslint-disable max-len */

import { FC, useLayoutEffect, useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Form, Typography, DatePicker } from 'antd';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { FiExternalLink } from 'react-icons/fi';
import SelectResponsible from 'components/TaskModal/components/SelectResponsible';
import SelectFollowers from 'components/TaskModal/components/SelectFollowers';
import LinkExternalIcon from 'components/LinkExternalIcon';
import FieldTimeRangeCustom from 'components/FieldTimeRangeCustom';
import RequestTimerDropdown from 'components/Timer/RequestTimerDropdown';
import { convertMinsToHrsMins, parseDateToUSFormat, renderTime } from 'utils/string.utils';
import { UserResponse } from 'types';
import { debounceAsync } from 'utils/debounce';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import appRoutes from 'config/appRoutes';
import { RequestFormProps, parseDataToSave } from '../RequestForm';
import styles from './requestUpdateNoDraft.module.less';

const { Text } = Typography;

interface RequestMetaFieldsProps {
  request: Record<string, any>;
  projectUuid?: string;
  isFreelancer: boolean;
  resetForm?: RequestFormProps['resetForm'];
  estimatedResetTrigger?: number;
  onSaveRequest: (values: any) => Promise<any>;
  onAddedTimelog: (timelog: any) => void;
  onSavingChange?: (saving: boolean) => void;
  setRequest?: (request: any) => void;
  hideGoToProject?: boolean;
}

const RequestMetaFields: FC<RequestMetaFieldsProps> = ({
  request,
  projectUuid,
  isFreelancer,
  resetForm,
  estimatedResetTrigger,
  onSaveRequest,
  onAddedTimelog,
  onSavingChange,
  setRequest,
  hideGoToProject
}) => {
  const user: UserResponse = useSelector((store: any) => store.auth.user);
  const [form] = Form.useForm();
  const [relatedUsers, setRelatedUsers] = useState(() => {
    const responsibles = request?.related_users?.filter((el: any) => el.pivot.type === 'responsible') ?? [];
    const followers = request?.related_users?.filter((el: any) => el.pivot.type === 'follower') ?? [];

    return { responsibles, followers };
  });

  const [saving, setSaving] = useState(false);
  const [dueDatePickerOpen, setDueDatePickerOpen] = useState(false);
  const dueDatePickerRef = useRef<any>(null);

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);

  const debounceUpdate = useMemo(() => {
    return debounceAsync(async (onUpdate: () => Promise<void>) => {
      return onUpdate();
    }, 800);
  }, []);

  const selectedProjectUuid = projectUuid ?? request?.project?.uuid;

  useLayoutEffect(() => {
    if (request?.related_users) {
      const responsibles = request.related_users.filter((el: any) => el.pivot.type === 'responsible');
      const followers = request.related_users.filter((el: any) => el.pivot.type === 'follower');

      setRelatedUsers({ responsibles, followers });
    }
  }, [request?.related_users]);

  useEffect(() => {
    if (resetForm) {
      const getNotDirtyKeys = (currentValues: Record<string, any>, initialValues: Record<string, any>) => {
        return Object.keys(currentValues).filter((key) => {
          const current = currentValues[key];
          const initial = initialValues[key];
          return JSON.stringify(current) === JSON.stringify(initial);
        });
      };

      form.resetFields(
        getNotDirtyKeys(form.getFieldsValue(), {
          estimated_time_range: resetForm.oldRequest.estimated_time_range,
          responsibles: resetForm.oldRequest.related_users.find((el: any) => el.pivot.type === 'responsible')?.uuid,
          followers: resetForm.oldRequest.related_users
            .filter((el: any) => el.pivot.type === 'follower')
            .map((el: any) => el.uuid),
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, resetForm]);

  useEffect(() => {
    if (estimatedResetTrigger) {
      form.resetFields(['estimated_time_range']);
    }
  }, [estimatedResetTrigger, form]);

  const onWrapUpdate = async (changedValues?: Record<string, any>) => {
    if (!onSaveRequest) return;

    setSaving(true);

    debounceUpdate(async () => {
      try {
        const allMainValues = await form.validateFields();

        const fieldsToSend: any = { ...changedValues };

        const responsibleChanged = 'responsibles' in (changedValues ?? {});
        const followersChanged = 'followers' in (changedValues ?? {});

        const responsibleUuid = allMainValues.responsibles || null;
        const followers: string[] = allMainValues.followers || [];

        if (responsibleChanged) {
          if (responsibleUuid) {
            const newFollowers = followers.filter((f: any) => f !== responsibleUuid);
            fieldsToSend.followers = newFollowers;
          }
        }

        if (followersChanged) {
          if (responsibleUuid && followers.includes(responsibleUuid)) {
            fieldsToSend.responsibles = null;
          } else {
            fieldsToSend.followers = followers;
          }
        }

        if (responsibleChanged || followersChanged) {
          if (!('responsibles' in fieldsToSend)) {
            fieldsToSend.responsibles = allMainValues.responsibles ?? null;
          }
          if (!('followers' in fieldsToSend)) {
            fieldsToSend.followers = followers;
          }
        }

        const updatePayload = parseDataToSave(fieldsToSend);

        await onSaveRequest(updatePayload);
      } catch (error) {
        asyncErrorHandler(error);
      } finally {
        setSaving(false);
      }
    });
  };

  return (
    <Form
      form={form}
      initialValues={{
        estimated_time_range: request.estimated_time_range,
        responsibles: request?.related_users?.find((el: any) => el.pivot.type === 'responsible')?.uuid,
        followers: request?.related_users?.filter((el: any) => el.pivot.type === 'follower').map((el: any) => el.uuid),
      }}
      labelAlign="left"
      layout="horizontal"
      labelCol={{ style: { flexShrink: 0 } }}
      onValuesChange={(changedValues) => {
        onWrapUpdate(changedValues);
      }}
    >
      <div className={styles.metaGridContainer}>
        <div className={styles.metaGrid}>
          <Form.Item label="Client" name="user_id" style={{ marginBottom: 0 }} className="client-field">
            <LinkExternalIcon to={`?client_uuid=${request?.project?.customer?.uuid}`} className="secondary" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {request?.project?.customer?.business_name}
            </LinkExternalIcon>
          </Form.Item>

          <div className="owner-field">
            <SelectResponsible
              label="Owner"
              form={form}
              state={relatedUsers}
              finalProjectId={selectedProjectUuid}
              setState={setRelatedUsers}
              disabled={!selectedProjectUuid || isFreelancer}
              required={false}
              planInput
              bordered
            />
          </div>

          <div className="followers-field">
            <SelectFollowers
              label="Followers"
              form={form}
              state={relatedUsers}
              finalProjectId={selectedProjectUuid}
              setState={setRelatedUsers}
              disabled={!selectedProjectUuid || isFreelancer}
              planInput
              bordered
            />
          </div>

          <div className="estimated-time-field">
            <FieldTimeRangeCustom
              name="estimated_time_range"
              label="Estimated time"
              disabled={isFreelancer}
              style={{ marginBottom: 0 }}
              planInput
            />
          </div>

          <Form.Item label="Spent" style={{ marginBottom: 0 }} className="spent-field">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Text type="secondary">{renderTime(convertMinsToHrsMins(request.time_spent))}</Text>
              {!isFreelancer && <RequestTimerDropdown request={request} onAdded={onAddedTimelog} icon="plus" />}
            </div>
          </Form.Item>

          <Form.Item label="Due date" style={{ marginBottom: 0 }} className="due-date-field">
            <DatePicker
              ref={dueDatePickerRef}
              value={request?.target_date ? moment.utc(request.target_date) : null}
              onChange={(date) => {
                const previousDate = request?.target_date;
                const newDate = date ? date.format('YYYY-MM-DD') : null;

                // Optimistic update
                setRequest?.({ ...request, target_date: newDate ? `${newDate}T00:00:00.000000Z` : null });

                // Close picker after selection
                setDueDatePickerOpen(false);

                // Debounced save with rollback on error
                debounceUpdate(async () => {
                  try {
                    await onSaveRequest({ target_date: newDate });
                  } catch (error) {
                    // Rollback on error
                    setRequest?.({ ...request, target_date: previousDate });
                    asyncErrorHandler(error);
                  }
                });
              }}
              format="MM/DD/YYYY"
              placeholder="no due date"
              disabledDate={(date) => !date.isAfter(moment(), 'day')}
              open={dueDatePickerOpen}
              onOpenChange={(open) => {
                setDueDatePickerOpen(open);
                if (!open) {
                  dueDatePickerRef.current?.input?.blur();
                }
              }}
            />
          </Form.Item>
        </div>
      </div>

      <div className={styles.metaFieldsFooter}>
        <Text type="secondary" className={styles.requestedBy}>
          Requested by {request?.requester?.name ?? 'No one'} at {parseDateToUSFormat(request?.created_at, true)}
        </Text>

        {
          !hideGoToProject && (
            <Link to={`${appRoutes.PROJECTS}/${request?.project?.uuid}`} className="primary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Go to project
              <FiExternalLink />
            </Link>
          )
        }
      </div>
    </Form>
  );
};

export default RequestMetaFields;
